import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Refrigerator,
  WashingMachine,
  Wind,
  Utensils,
  Flame,
  Microwave,
  Snowflake,
  Droplets,
  Wrench,
  ArrowRight,
  CheckCircle2,
  Phone,
  CalendarCheck,
} from "lucide-react";
import { BUSINESS } from "@/lib/contact";
import imgFridge from "@/assets/appliances/stock_fridge.jpg";
import imgWasher from "@/assets/appliances/stock_washer.jpg";
import imgDryer from "@/assets/appliances/stock_dryer.jpg";
import imgDishwasher from "@/assets/appliances/stock_dish.jpg";
import imgOven from "@/assets/appliances/stock_oven.jpg";
import imgMicrowave from "@/assets/appliances/stock_micro.jpg";

export const Route = createFileRoute("/services")({
  component: ServicesPage,
  head: () => ({
    meta: [
      { title: "Appliance Repair Services | StillFusion Sacramento" },
      {
        name: "description",
        content:
          "Refrigerator, washer, dryer, dishwasher, oven, and microwave repair in Sacramento and surrounding cities. Same-day service from licensed technicians.",
      },
      { property: "og:title", content: "Appliance Repair Services | StillFusion" },
      { property: "og:description", content: "All major appliance repairs across the Sacramento area." },
      { property: "og:url", content: "https://stillfusionappliance.com/services" },
    ],
    links: [
      { rel: "canonical", href: "https://stillfusionappliance.com/services" },
    ],
  }),
});

const services = [
  {
    icon: Refrigerator,
    image: imgFridge,
    title: "Refrigerator Repair",
    blurb: "Keep your food fresh and your kitchen running. We service all brands and styles — from basic top-freezers to French-door and built-in units.",
    issues: ["Not cooling or freezing", "Water leaking on the floor", "Loud humming or clicking", "Ice maker not working", "Frost build-up in freezer"],
  },
  {
    icon: WashingMachine,
    image: imgWasher,
    title: "Washer Repair",
    blurb: "Top-load and front-load washers. We diagnose drainage, balance, and electronic faults and replace worn parts on the spot.",
    issues: ["Won't spin or drain", "Leaking water during cycle", "Loud banging or shaking", "Door won't unlock", "Error codes on display"],
  },
  {
    icon: Wind,
    image: imgDryer,
    title: "Dryer Repair",
    blurb: "Gas and electric dryers. We replace heating elements, belts, thermostats and clear vent issues that cause long dry times.",
    issues: ["Not heating", "Takes too long to dry", "Won't start or tumble", "Loud or squeaking", "Burning smell"],
  },
  {
    icon: Utensils,
    image: imgDishwasher,
    title: "Dishwasher Repair",
    blurb: "From pump replacements to clogged spray arms — we get your dishwasher cleaning properly again without overpaying for parts.",
    issues: ["Not draining or filling", "Dishes still dirty", "Leaking under the unit", "Won't start a cycle", "Loud grinding noise"],
  },
  {
    icon: Flame,
    image: imgOven,
    title: "Oven, Stove & Cooktop",
    blurb: "Gas, electric, and induction. We replace igniters, bake elements, control boards and burners across all major brands.",
    issues: ["Oven won't heat or stays cold", "Burners won't light", "Uneven baking", "Self-clean stuck", "F-codes on the display"],
  },
  {
    icon: Microwave,
    image: imgMicrowave,
    title: "Microwave Repair",
    blurb: "Over-the-range and built-in microwaves. We service magnetrons, door switches, and control panels — and tell you honestly when replacement is smarter.",
    issues: ["Not heating food", "Sparking inside", "No power or display", "Turntable won't spin", "Loud buzzing"],
  },
];

const extras = [
  { icon: Snowflake, title: "Freezers & Ice Makers" },
  { icon: Droplets, title: "Wine Coolers" },
  { icon: Wrench, title: "Garbage Disposals" },
  { icon: CheckCircle2, title: "Range Hoods" },
];

function ServicesPage() {
  return (
    <>
      {/* Hero */}
      <section className="border-b border-border bg-secondary/40">
        <div className="mx-auto max-w-6xl px-5 py-16 sm:py-20">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary">
              Services
            </span>
            <h1 className="mt-4 text-4xl font-bold tracking-tight sm:text-5xl">
              Full-service appliance repair in Sacramento
            </h1>
            <p className="mt-4 text-lg text-muted-foreground">
              Same-day and next-day appointments for all major household
              appliances. Honest diagnostics, upfront pricing, and quality parts.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/contact" className="btn-accent inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold">
                <CalendarCheck className="h-4 w-4" /> Book Service
              </Link>
              <a href={`tel:${BUSINESS.phone}`} className="btn-outline inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold">
                <Phone className="h-4 w-4" /> Call Now
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Service cards */}
      <section className="mx-auto max-w-6xl px-5 py-20">
        <div className="grid gap-6 md:grid-cols-2">
          {services.map(({ icon: Icon, image, title, blurb, issues }) => (
            <article key={title} className="surface-card overflow-hidden">
              <div className="relative aspect-[16/9] overflow-hidden">
                <img src={image} alt={`${title} service in Sacramento`} className="h-full w-full object-cover" loading="lazy" />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/45 to-transparent" />
                <span className="absolute left-4 top-4 inline-flex items-center gap-2 rounded-full bg-white/90 px-3 py-1.5 text-xs font-semibold text-primary backdrop-blur">
                  <Icon className="h-4 w-4" /> {title}
                </span>
              </div>
              <div className="p-6">
                <h2 className="text-lg font-semibold tracking-tight">{title}</h2>
                <p className="text-sm leading-relaxed text-muted-foreground">{blurb}</p>
                <ul className="mt-5 grid gap-2 sm:grid-cols-2">
                  {issues.map((i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                      <span>{i}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/contact"
                  className="mt-6 inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
                  aria-label={`Book ${title} in Sacramento`}
                >
                  Book this repair <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Extras */}
      <section className="border-t border-border bg-secondary/40">
        <div className="mx-auto max-w-6xl px-5 py-16">
          <div className="text-center">
            <span className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary">
              More appliances
            </span>
            <h2 className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl">
              And a few more
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-muted-foreground">
              Don't see your appliance? Just give us a call — chances are we fix it.
            </p>
          </div>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {extras.map(({ icon: Icon, title }) => (
              <div key={title} className="surface-card flex items-center gap-3 p-5">
                <span className="grid h-10 w-10 place-items-center rounded-lg bg-primary/10 text-primary">
                  <Icon className="h-5 w-5" />
                </span>
                <span className="text-sm font-semibold">{title}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-6xl px-5 py-20">
        <div className="surface-elevated bg-hero-gradient p-10 text-center text-white sm:p-14">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
            Ready to book a repair?
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-white/80">
            Get a same-day appointment — call, text, or send a quick request.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to="/contact" className="btn-accent inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold">
              <CalendarCheck className="h-4 w-4" /> Book Service
            </Link>
            <a href={`tel:${BUSINESS.phone}`} className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/10 px-6 py-3 text-sm font-semibold backdrop-blur transition hover:bg-white/15">
              <Phone className="h-4 w-4" /> Call Now
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
